/**
 * The painter: real light instead of computed tones.
 *
 * Ported from Armarium (`apps/web/src/lib/dice3d/three-renderer.ts`), trimmed
 * to what the initiative roll-off needs (no sparks, no light/dark sheet).
 * What gives an expensive die its look cannot be painted per face:
 *
 * 1. **Environment reflection.** A studio room (`RoomEnvironment`) sits behind
 *    the material as a mirror world.
 * 2. **The body itself**: chamfered geometry, card-stock atlas and bump map.
 *    Shared with the game board, see `dieAssets.js`.
 * 3. **A real cast shadow**, in the die's own silhouette, softening with height.
 *
 * The maths stays where it was: `geometry.js` supplies the body, `animate.js`
 * the path. Here it is only drawn.
 */

import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

import { FLOOR_Y, restHeight, STAGE_X, STAGE_Z } from './animate';
import { cloneDieMaterial, DICE_PALETTES, dieAssets, loadDiceArtwork, redrawDiceArtwork, skinDieAssets } from './dieAssets';
import { Sparks } from './sparks';

export { loadDiceArtwork };

/**
 * The view from above: the camera hangs almost vertically over the table, set
 * forward only far enough that the body of the die stays readable. The rolled
 * face lies on top and faces the viewer.
 */
const CAMERA = { fov: 26, direction: [0, 7.0, 2.2] };
/**
 * The studio the initiative roll is lit by: a small photographic room, so the
 * relief and gloss the dice carry have something real to reflect. Loaded
 * after the first frame; until then the synthetic white room stands in.
 */
export const STUDIO_ENVIRONMENT_URL = '/game-assets/dice/env/studio_small_09_1k.hdr';

const KEY_INTENSITY = 0.95;

/** A finger's width of air so no body overruns the container edge. */
const WALL_MARGIN = 0.12;

/** How hard a cushion bang nudges the camera, and how fast that ebbs away. */
const SHAKE_PER_HIT = 0.03;
const SHAKE_DECAY = 9;

/**
 * **The motion blur.**
 *
 * The smear is made from copies of the same pose, a few milliseconds earlier.
 * Their spacing must **not** be measured in milliseconds: at 60 rad/s twelve
 * milliseconds are a good 40 degrees apart, and five such copies are not a
 * smear but a stack of prints. So the spacing is measured in **angle**. The
 * chain always sweeps the same arc no matter how fast the die spins, and only
 * gets denser.
 */
const GHOSTS = 8;
/** The arc the whole chain sweeps (rad). */
const SMEAR_ARC = 1.15;
/** From this angular speed (rad/s) the smear begins. */
const BLUR_FROM = 9;
/**
 * **The shadow smears too**, but not all eight ghosts need to cast it: a shadow
 * has no half-transparency, so the result is the union of the silhouettes, and
 * three copies spread over the chain give the same union as eight.
 */
const SHADOW_GHOSTS = 3;
const GHOST_AXIS = new THREE.Vector3();
const GHOST_TURN = new THREE.Quaternion();
/** The four screen corners and one scratch vector. Allocated once. */
const CORNERS = [[-1, -1], [1, -1], [-1, 1], [1, 1]];
const PROBE = new THREE.Vector3();

/**
 * **The shadow softens the higher the die stands.** Close over the table the
 * umbra is narrow and dark; up high it dissolves into a breath.
 */
const SHADOW_SHARP = { blur: 13, opacity: 0.34 };
const SHADOW_SOFT = { blur: 34, opacity: 0.16 };

/**
 * **Where the key light stands.**
 *
 * At 75° elevation it vanished completely under the body that casts it. At a
 * good 60° the silhouette falls next to the die where its shape can be read,
 * and still clings to the body instead of running across the table.
 *
 * And it stands back-left, not front-left: the camera looks down from the
 * front, so depth (-z) is *up* on screen. A light from the front threw the
 * shadow **upward** behind the die; a top-down view wants it falling downward.
 */
const KEY_AT = [-1.7, 6.0, -1.7];

/** How much a losing die is muted. Multiplies the card texture. */
const DIM_COLOR = 0x6a625a;

/** The colours of the tinder: hot core, cold ash. One pair per occasion. */
const EMBER = {
  wall: { hot: 0xfff3d0, cool: 0xd9903c },
  land: { hot: 0xffe9b8, cool: 0xc78a3a },
  high: { hot: 0xfffbe8, cool: 0xd9b45c },
  low: { hot: 0xffb08a, cool: 0x7a2f22 },
};

export class DiceGL {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {number} viewHalfWidth  Half the world width the camera frames.
   *   Larger means a smaller die on a wider table.
   */
  constructor(canvas, viewHalfWidth = 2.72) {
    this.viewHalfWidth = viewHalfWidth;
    this.meshes = [];
    this.dieMats = [];
    this.ghosts = [];
    this.ghostMats = [];
    this.shake = 0;
    /** The measured camera distance and the visible half-extents below it. */
    this.reach = 1;
    this.half = [STAGE_X, STAGE_Z];
    /** Where the camera looks: the depth at which the dice come to rest. */
    this.focusZ = 0;
    this.lastTime = null;

    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.shadowMap.enabled = true;
    // VSM: the only shadow map with a genuinely soft edge. At a couple of dice
    // its cost is a non-issue.
    this.renderer.shadowMap.type = THREE.VSMShadowMap;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(CAMERA.fov, 1, 0.5, 80);
    this.aim(1, 0);

    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    // The mirror world is baked **once**; the room it came from has nothing
    // left to do afterwards and gives its meshes straight back.
    const room = new RoomEnvironment();
    this.envRT = this.pmrem.fromScene(room, 0.04);
    room.dispose();
    this.scene.environment = this.envRT.texture;
    this.disposed = false;
    /** Resolves once the studio HDR is baked in (or failed and the white room stays). */
    this.environmentReady = this.loadEnvironment(STUDIO_ENVIRONMENT_URL);

    this.key = new THREE.DirectionalLight(0xfff0da, KEY_INTENSITY);
    this.key.position.set(...KEY_AT);
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(2048, 2048);
    this.key.shadow.camera.near = 0.5;
    this.key.shadow.camera.far = 14;
    this.key.shadow.radius = SHADOW_SHARP.blur;
    this.key.shadow.blurSamples = 24;
    this.key.shadow.bias = -0.0004;
    this.scene.add(this.key);

    const fill = new THREE.DirectionalLight(0xd8c4a0, 0.62);
    fill.position.set(2.4, 1.2, 2.6);
    this.scene.add(fill);

    // The table: invisible but for the shadow that falls on it.
    this.floorMat = new THREE.ShadowMaterial({
      color: 0x000000,
      opacity: SHADOW_SHARP.opacity,
    });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(24, 24), this.floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = FLOOR_Y;
    floor.receiveShadow = true;
    this.scene.add(floor);

    this.sparks = new Sparks(this.scene, FLOOR_Y + 0.02);
    /** Who has already landed. The landing only fires once. */
    this.landed = [];

    // **Wipe the canvas before anyone sees it.** Baking the mirror world draws,
    // and it draws bright. `RoomEnvironment` is a white room. What is left in
    // the frame buffer stays there until the first real frame goes over it.
    this.renderer.setRenderTarget(null);
    this.renderer.clear();
  }

  /**
   * Swap the reflection for an equirectangular HDR. The current environment
   * stays up until the new one is baked, and a load that lands after dispose
   * is thrown away. Subclasses that light themselves differently override.
   */
  async loadEnvironment(url) {
    let texture;
    try {
      texture = await new RGBELoader().loadAsync(url);
    } catch {
      return false;
    }
    if (this.disposed) {
      texture.dispose();
      return false;
    }
    texture.mapping = THREE.EquirectangularReflectionMapping;
    const baked = this.pmrem.fromEquirectangular(texture);
    texture.dispose();
    this.envRT.dispose();
    this.envRT = baked;
    this.scene.environment = baked.texture;
    return true;
  }

  /**
   * One mesh plus a ghost chain per die. `palette` is a DICE_PALETTES id the
   * dice wear instead of the player's own pick: the initiative roll shows
   * each seat's d20 in that seat's palette on both clients. Unknown or
   * absent, the dice follow the player's pick.
   */
  setPlan(count, palette = null) {
    this.clearMeshes();
    this.landed = new Array(count).fill(false);

    const recipe = palette ? DICE_PALETTES[palette] : null;
    const { geometry, material } = recipe ? skinDieAssets(20, 2, recipe) : dieAssets(20, 2);
    for (let i = 0; i < count; i++) {
      const mat = cloneDieMaterial(material);
      const mesh = new THREE.Mesh(geometry, mat);
      mesh.castShadow = true;
      this.scene.add(mesh);
      this.meshes.push(mesh);
      this.dieMats.push(mat);

      // The ghosts share mesh and textures, but not the opacity.
      const chain = [];
      const mats = [];
      for (let g = 0; g < GHOSTS; g++) {
        const ghostMat = cloneDieMaterial(material);
        ghostMat.transparent = true;
        ghostMat.opacity = 0;
        ghostMat.depthWrite = false;
        const ghost = new THREE.Mesh(geometry, ghostMat);
        ghost.visible = false;
        // Spread over the chain, not the first three: the shadow should sweep
        // the whole smear, not just its start.
        ghost.castShadow = g % Math.ceil(GHOSTS / SHADOW_GHOSTS) === 0;
        this.scene.add(ghost);
        chain.push(ghost);
        mats.push(ghostMat);
      }
      this.ghosts.push(chain);
      this.ghostMats.push(mats);
    }
  }

  clearMeshes() {
    for (const mesh of this.meshes) this.scene.remove(mesh);
    for (const mat of this.dieMats) mat.dispose();
    for (const chain of this.ghosts) for (const ghost of chain) this.scene.remove(ghost);
    for (const chain of this.ghostMats) for (const mat of chain) mat.dispose();
    this.meshes = [];
    this.dieMats = [];
    this.ghosts = [];
    this.ghostMats = [];
  }

  /**
   * **Sparks only happen at the wall.**
   *
   * They once flew on every bounce on the table too. That was well meant and
   * read as randomness: it flashed here and there without you seeing *at what*.
   * A spark is information. It says something hard was struck. On the table
   * the die strikes nothing, it merely lands; at the wall it bangs, and that is
   * where it should spray.
   */
  strike(anim) {
    const hit = anim.impact;
    if (hit === null || hit.kind !== 'wall' || hit.strength < 0.25) return;
    // **Sparks stay at the wall.** `speed / drag` is the range; here it sits at
    // a good third of a unit, i.e. at the edge. Flying further, they would be
    // born at the rim and seen in the middle. Which reads as sparks out of
    // nowhere even though every one came from a wall hit.
    this.sparks.emit({
      count: Math.round(12 + hit.strength * 24),
      at: hit.at,
      dir: [hit.normal[0], 0.5, hit.normal[2]],
      spread: 0.38,
      shell: 0.14,
      speed: 1.3 + hit.strength * 1.4,
      drag: 7,
      // **Short.** An impact is a flash, not a smoulder. Anything glowing for
      // three tenths of a second ends up beside a wall the die has long left.
      life: 0.14,
      size: 0.23,
      ...EMBER.wall,
      gravity: 5.5,
    });
  }

  /**
   * **The landing is the firework.**
   *
   * When the die stops, it dusts. A burst, not a trickle. The natural 20 gets
   * it a size larger, but every throw gets it: this is the moment the whole
   * path was running toward.
   */
  land(anim, crit) {
    const grand = crit === 'high';
    const ember = crit === 'high' ? EMBER.high : crit === 'low' ? EMBER.low : EMBER.land;
    this.sparks.emit({
      count: grand ? 150 : crit === 'low' ? 52 : 84,
      at: [anim.p[0], anim.p[1] + anim.radius * 0.1, anim.p[2]],
      dir: [0, 1, 0],
      spread: grand ? 0.8 : 0.9,
      shell: anim.radius * (grand ? 0.95 : 0.7),
      speed: grand ? 3 : 2,
      // The finale may scatter: that is where the die *is*, nothing comes out
      // of nowhere. So only it keeps the long range.
      drag: grand ? 1.9 : 2.6,
      life: grand ? 1 : 0.62,
      size: grand ? 0.3 : 0.22,
      ...ember,
      gravity: grand ? 2.4 : 3.6,
    });
  }

  /** Mute one die. The loser's, once the roll-off has resolved. */
  setDim(index, dim) {
    const mats = [this.dieMats[index], ...(this.ghostMats[index] ?? [])];
    for (const mat of mats) {
      if (mat) mat.color.setHex(dim ? DIM_COLOR : 0xffffff);
    }
  }

  /** Camera at `reach` times base distance, looking at `focusZ` in depth. */
  aim(reach, focusZ, jitter = 0) {
    this.camera.position.set(
      CAMERA.direction[0] * reach + jitter,
      CAMERA.direction[1] * reach,
      CAMERA.direction[2] * reach + focusZ + jitter,
    );
    this.camera.lookAt(0, 0, focusZ);
    this.camera.updateMatrixWorld();
  }

  /**
   * Where the ray through screen point `(sx, sy)` meets the resting plane. This
   * is how what the camera actually sees gets read off instead of derived from
   * field of view and tilt.
   */
  groundAt(sx, sy, into) {
    into.set(sx, sy, 0.5).unproject(this.camera).sub(this.camera.position);
    const t = -this.camera.position.y / into.y;
    return into.multiplyScalar(t).add(this.camera.position);
  }

  /** The visible half-extents around `focusZ`. The tightest corner counts. */
  visible(focusZ) {
    let x = Infinity;
    let z = Infinity;
    for (const [sx, sy] of CORNERS) {
      const p = this.groundAt(sx, sy, PROBE);
      x = Math.min(x, Math.abs(p.x));
      z = Math.min(z, Math.abs(p.z - focusZ));
    }
    return [x, z];
  }

  /** Where world origin lands on screen, in NDC (-1…1). */
  originOnScreen() {
    return PROBE.set(0, 0, 0).project(this.camera).y;
  }

  /**
   * **Fit the container.**
   *
   * Two quantities fall out here: the distance (far enough that the same width
   * of table is always visible) and the depth of the look-at point (so the dice
   * rest where `focus` asks). Both are **measured, not computed**, and both are
   * linear in the sought value. One probe shot each is enough.
   */
  fitToCanvas(focus) {
    this.aim(1, 0);
    const [probeX] = this.visible(0);
    this.reach = probeX > 1e-6 ? this.viewHalfWidth / probeX : 1;

    const want = 1 - 2 * focus;
    this.aim(this.reach, 0);
    const at0 = this.originOnScreen();
    this.aim(this.reach, 1);
    const at1 = this.originOnScreen();
    this.focusZ = Math.abs(at1 - at0) < 1e-6 ? 0 : (want - at0) / (at1 - at0);

    this.aim(this.reach, this.focusZ);
    const [x, z] = this.visible(this.focusZ);
    this.half = [Math.max(0.4, x - WALL_MARGIN), Math.max(0.4, z - WALL_MARGIN)];

    // The shadow needs a frame around the whole stage, or a die at the wall
    // loses its shadow.
    const edge = Math.max(this.half[0], this.half[1]) + 1;
    this.key.shadow.camera.left = -edge;
    this.key.shadow.camera.right = edge;
    this.key.shadow.camera.top = edge;
    this.key.shadow.camera.bottom = -edge;
    this.key.shadow.camera.far = 14 * this.reach;
    this.key.position.set(
      KEY_AT[0] * this.reach,
      KEY_AT[1] * this.reach,
      KEY_AT[2] * this.reach + this.focusZ,
    );
    this.key.target.position.set(0, 0, this.focusZ);
    this.key.target.updateMatrixWorld();
    this.key.shadow.camera.updateProjectionMatrix();
  }

  /**
   * The stage as the throw knows it: half width and half depth in world units.
   * `animate.js` puts its walls exactly there.
   */
  stage() {
    return this.half;
  }

  setSize(width, height, dpr, focus = 0.5) {
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.fitToCanvas(focus);
    // Sparks are sized in pixels, not world units. The conversion hangs on
    // exactly this height.
    this.sparks.setViewport(height * dpr, CAMERA.fov);
  }

  /**
   * One frame: take the pose from the simulation and draw.
   *
   * @param dice      the `DieAnim` array from `animate.js`
   * @param emphasis  0…1 settle gleam on the key light
   * @param crits     per-die `'high' | 'low' | null`, known **before** the die
   *                  lands. The firework has to fire on touchdown, not after.
   */
  render(dice, emphasis, crits = []) {
    const now = performance.now() / 1000;
    const dt = this.lastTime === null ? 0 : Math.min(0.05, now - this.lastTime);
    this.lastTime = now;

    for (let i = 0; i < this.meshes.length; i++) {
      const anim = dice[i];
      const mesh = this.meshes[i];
      if (anim === undefined) continue;
      // The waiting die lies there visibly; the delay hides nothing any more.
      // it is the holding still before the flick.
      mesh.position.set(anim.p[0], anim.p[1], anim.p[2]);
      mesh.quaternion.set(anim.q.x, anim.q.y, anim.q.z, anim.q.w);
      mesh.scale.setScalar(anim.radius);

      this.strike(anim);
      if (anim.impact?.kind === 'settle' && !this.landed[i]) {
        this.landed[i] = true;
        this.land(anim, crits[i] ?? null);
      } else if (anim.phase === 'throw' && anim.t < 0.2) {
        this.landed[i] = false;
      }

      // Motion blur: the same pose a bit of rotation earlier. The spacing lives
      // in the angle, not in time (see `SMEAR_ARC`).
      const w = anim.w;
      const speed = Math.hypot(w[0], w[1], w[2]);
      const chain = this.ghosts[i]?.length ?? 0;
      const trail = speed > BLUR_FROM ? Math.min(chain, Math.round(speed / BLUR_FROM) + 2) : 0;
      const step = trail > 0 ? SMEAR_ARC / (trail * speed) : 0;
      for (let g = 0; g < chain; g++) {
        const ghost = this.ghosts[i][g];
        const mat = this.ghostMats[i][g];
        if (g >= trail) {
          ghost.visible = false;
          continue;
        }
        const back = (g + 1) * step;
        ghost.visible = true;
        GHOST_AXIS.set(w[0] / speed, w[1] / speed, w[2] / speed);
        GHOST_TURN.setFromAxisAngle(GHOST_AXIS, -speed * back);
        ghost.quaternion.copy(GHOST_TURN).multiply(mesh.quaternion);
        ghost.position.set(
          anim.p[0] - anim.v[0] * back,
          anim.p[1] - anim.v[1] * back,
          anim.p[2] - anim.v[2] * back,
        );
        // The ghosts swell minimally: the smear goes soft instead of looking
        // like stacked prints.
        ghost.scale.setScalar(anim.radius * (1 + (g + 1) * 0.012));
        mat.opacity = 0.62 * (1 - (g + 1) / (trail + 1)) ** 1.35;
      }
    }

    // On coming to rest the light pulls up briefly. The gleam of the result.
    this.key.intensity = KEY_INTENSITY * (1 + 0.22 * emphasis);

    // **Penumbra.** The highest body decides how softly it is drawn: a shadow
    // has one softness, not two, and the flying die is the one being watched.
    let high = 0;
    for (const anim of dice) {
      high = Math.max(high, (anim.p[1] - restHeight(anim.radius)) / (anim.radius * 2.6));
    }
    const soft = Math.min(1, Math.max(0, high));
    this.key.shadow.radius = SHADOW_SHARP.blur + (SHADOW_SOFT.blur - SHADOW_SHARP.blur) * soft;
    this.floorMat.opacity = SHADOW_SHARP.opacity
      + (SHADOW_SOFT.opacity - SHADOW_SHARP.opacity) * soft;

    this.placeCamera(dice, dt);
    this.sparks.step(dt);

    this.renderer.render(this.scene, this.camera);
  }

  /**
   * A tremor when the die just hit the cushion. It stays tiny: it should
   * confirm the bang, not shake the frame. And above all it must not move the
   * walls the die bounces off.
   */
  placeCamera(dice, dt) {
    let bang = 0;
    for (const anim of dice) {
      if (anim.impact?.kind === 'wall') bang = Math.max(bang, anim.impact.strength);
    }
    this.shake = Math.max(this.shake * Math.exp(-SHAKE_DECAY * dt), bang * SHAKE_PER_HIT);
    this.aim(this.reach, this.focusZ, (Math.random() - 0.5) * this.shake * this.reach);
  }

  /** Once numeral sheet and card stock arrive, the faces are re-cut. */
  refreshArtwork() {
    redrawDiceArtwork();
  }

  dispose() {
    this.clearMeshes();
    this.sparks.clear();
    this.floorMat.dispose();
    this.disposed = true;
    this.envRT.dispose();
    this.pmrem.dispose();
    this.renderer.dispose();
  }
}
